import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';
import { WidgetService } from '@shared/services/widget.service';

import { commonChartTooltip } from '@shared/variables/echart-common.variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption } from 'echarts';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-azure-devops-sprints-widget',
  templateUrl: './azure-devops-sprints-widget.component.html'
})
export class AzureDevopsSprintsWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: [] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  dateFormat: string;

  chartOption: EChartOption;

  chartData: SprintsWidget.SprintsWidgetModel;

  @ViewChild('toolTipTitle') toolTipTitle: TemplateRef<any>;
  toolTipHTML: string;

  constructor(private widgetService: WidgetService,
              private customEventService: CustomEventService,
              private globalSettingsService: GlobalSettingsService) { }

  ngOnInit(): void {
    this.dateFormat = this.globalSettingsService.dateFormat;

    this.toolTipHTML = this.headerComponents.join('<br>');


    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }

    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }
        this.showWidgetStatePlaceholder = undefined;
        this.chartData = response;

        if (this.chartData.data.sprints > 1) {
          this.chartData.data.values = this.chartData.data.values.map(item => {
            return {
              ...item,
              type: 'bar',
              stack: 'sprint',
              itemStyle: {
                color: `#${item.color}`
              }
            }
          });

          const chartData = this.chartData.data.values.map(item => {
            return {
              name: item.name,
            }
          });

          this.chartOption = {
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              }
            },
            legend: {
              data: chartData
            },
            grid: {
              left: '3%',
              right: '4%',
              bottom: '3%',
              containLabel: true
            },
            xAxis: [
              {
                type: 'category',
                data: this.chartData.data.sprintNames
              }
            ],
            yAxis: [
              {
                type: 'value'
              }
            ],
            series: this.chartData.data.values
          };
        } else {
          const chartData = this.chartData.data.values.map(item => {
            return {
              name: item.name,
              value: item.value
            }
          });

          const colorArray = this.chartData.data.values.map(data => `#${data.color}`);

          this.chartOption = {
            color: colorArray,
            tooltip: {
              ...commonChartTooltip,
              formatter: '{b} : {c} ({d}%)'
            },
            grid: {
              top: 0,
              bottom: 0,
              right: 0,
              left: 0
            },
            legend: {
              type: 'scroll',
              orient: 'horizontal',
              data: chartData
            },
            series: [
              {
                type: 'pie',
                center: ['50%', '55%'],
                label: {
                  normal: {
                    show: false,
                  }
                },
                data: chartData,
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                  }
                }
              }
            ]
          };
        }

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  onChartEvent($event): void {
    if (this.chartData.data.urls && this.chartData.data.urls[$event.dataIndex]) {
      const fullUrl = this.chartData.data.urls[$event.dataIndex];
      const match = fullUrl.match(/(\/agile-space\/[a-zA-Z0-9]+)/);
      const matchSpritId = fullUrl.match(/\/sprints\/([a-zA-Z0-9]+)/);
      if (match) {
        const sprintId = matchSpritId[1];
        const newUrl = match[0];
        window.open(`${newUrl}?activeTab=1&sprintId=${sprintId}`, '_blank');
      }
    }
  }

}
